import { Inject, Injectable } from '@angular/core';
import {
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationModelFromJson,
    JsonConfirmationModel,
    LZStringService,
    NotificationIdentifierModel,
    UserMasterService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import Dexie from 'dexie';
import { Observable } from 'rxjs';
import { StorageConfirmationInterface } from './storage-confirmation.interface';
import { EncryptionService } from '@evorait/pace-encryption';

interface DatabaseConfirmation {
    frontid: string;
    qmnum: string;
    manum: string;
    timestamp: number;
    confirmationblob: Uint8Array;
}

@Injectable({
    providedIn: 'root',
})
export class StorageConfirmationService extends Dexie implements StorageConfirmationInterface {
    private static readonly VERSION_ONE = 1;
    private confirmations: Dexie.Table<DatabaseConfirmation, number>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly encryption: EncryptionService,
        private readonly LZString: LZStringService,
        private readonly userMasterService: UserMasterService,
    ) {
        super('ConfirmationDatabase');
        this.version(StorageConfirmationService.VERSION_ONE).stores({
            confirmations: '[frontid+qmnum+manum]',
        });
    }

    public saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel> {
        const json = JSON.stringify(confirmation);
        let compressed = this.LZString.compressToUint8Array(json);
        if (this.userMasterService.isPaceOnlineUser()) {
            compressed = this.encryption.encryptAndCompress(json);
        }

        return new Observable((subscriber) => {
            this.confirmations
                .put({
                    frontid: confirmation.notificationIdentifier.getFrontId(),
                    qmnum: confirmation.notificationIdentifier.getQmnum(),
                    manum: confirmation.notificationIdentifier.getManum(),
                    timestamp: confirmation.saveTimestamp,
                    confirmationblob: compressed,
                })
                .then(() => {
                    subscriber.next(confirmation);
                    subscriber.complete();
                })
                .catch((error) => {
                    const errorMessage = `[ConfirmationDatabaseService](saveConfirmation): Confirmation with following key could not be saved to database: ${confirmation.notificationIdentifier.toString()}. Error ${error}`;
                    this.log.error(errorMessage);
                    subscriber.error(new Error(errorMessage));
                });
        });
    }

    public removeConfirmation(confirmation: ConfirmationModel): Observable<boolean> {
        return new Observable((subscriber) => {
            const id = confirmation.notificationIdentifier;
            this.confirmations
                .where({
                    frontid: id.getFrontId(),
                    qmnum: id.getQmnum(),
                    manum: id.getManum(),
                })
                .delete()
                .then((result: number) => {
                    if (result === 1) {
                        subscriber.next(true);
                        subscriber.complete();
                    } else {
                        const errorMessage = `[ConfirmationDatabaseService](saveConfirmation): Confirmation with following key could not be deleted from database: ${id.toString()}`;
                        this.log.error(errorMessage);
                        subscriber.error(new Error(errorMessage));
                    }
                })
                .catch((error) => {
                    const errorMessage = `[ConfirmationDatabaseService](saveConfirmation): Confirmation with following key could not be deleted from database: ${id.toString()}. Error ${error}`;
                    this.log.error(errorMessage);
                    subscriber.error(new Error(errorMessage));
                });
        });
    }

    public getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel> {
        return new Observable((subscriber) => {
            this.confirmations
                .get({
                    frontid: id.getFrontId(),
                    qmnum: id.getQmnum(),
                    manum: id.getManum(),
                })
                .then((databaseConfirmation) => {
                    if (!databaseConfirmation) {
                        const errorMessage = `[ConfirmationDatabaseService](saveConfirmation): Confirmation with following key could not be found in database: ${id.toString()}`;
                        this.log.warn(errorMessage);
                        subscriber.error(errorMessage);
                    } else {
                        let data = this.LZString.decompressFromUint8Array(databaseConfirmation.confirmationblob);
                        if (this.userMasterService.isPaceOnlineUser()) {
                            // In case of PACEOnline the data is encrypted and we need to decrypt and decompress
                            data = this.encryption.decryptAndDecompress(databaseConfirmation.confirmationblob);
                        }
                        subscriber.next(new ConfirmationModelFromJson(<JsonConfirmationModel>JSON.parse(data), type));
                        subscriber.complete();
                    }
                })
                .catch((error) => {
                    const errorMessage = `[ConfirmationDatabaseService](saveConfirmation): Confirmation with following key could not be read from database: ${id.toString()}. Error ${error}`;
                    this.log.error(errorMessage);
                    subscriber.error(new Error(errorMessage));
                });
        });
    }
}
